import React, { useState, useRef, useEffect } from "react";
import { Phone, Mail, Instagram, Linkedin, Send, CheckCircle, MessageCircle } from "lucide-react";
import SectionHeading from "../UIComponents/SectionHeading";
import Button from "../UIComponents/Button";
import { db } from "../Firebase/firebaseConfig";
import { collection, addDoc, Timestamp } from "firebase/firestore";


import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ContactSection = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        message: ""
    });

    const [errors, setErrors] = useState({});
    const [focusedField, setFocusedField] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // ⭐ Animation refs
    const leftCardRef = useRef(null);
    const rightCardRef = useRef(null);
    const ctxRef = useRef(null);
    const observerRef = useRef(null);

    /* ────────────────────────────────────────────────
       GSAP SCROLL ANIMATIONS
    ───────────────────────────────────────────────── */
    useEffect(() => {
        const leftCard = leftCardRef.current;
        const rightCard = rightCardRef.current;
        if (!leftCard || !rightCard) return;

        ctxRef.current = gsap.context(() => {
            gsap.set([leftCard, rightCard], {
                autoAlpha: 0,
                y: 80,
                filter: "blur(7px)",
            });

            const animateCards = () => {
                gsap.to([leftCard, rightCard], {
                    autoAlpha: 1,
                    y: 0,
                    filter: "blur(0px)",
                    duration: 0.6,
                    ease: "power4.out",
                    stagger: 0.2,
                });
            };

            // If ScrollTrigger works (browser or production)
            if (typeof ScrollTrigger !== "undefined") {
                ScrollTrigger.create({
                    trigger: leftCard,
                    start: "top 95%",
                    once: true,
                    onEnter: animateCards,
                });

                setTimeout(() => ScrollTrigger.refresh(), 300);
            } else {
                // Fallback: IntersectionObserver for static builds
                const io = new IntersectionObserver((entries, obs) => {
                    if (entries[0].isIntersecting) {
                        animateCards();
                        obs.disconnect();
                    }
                });

                observerRef.current = io;
                io.observe(leftCard);
            }
        });

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
            if (ctxRef.current) ctxRef.current.revert();
        };
    }, []);

    /* ─────────────────────────────────────────────── */

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });

        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const validateForm = () => {
        let newErrors = {};

        if (!form.name.trim()) newErrors.name = "Full name required";
        if (!form.email.trim()) {
            newErrors.email = "Email required";
        } else if (!/\S+@\S+\.\S+/.test(form.email)) {
            newErrors.email = "Invalid email format";
        }
        if (!form.message.trim()) newErrors.message = "Message required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        // 1️⃣ Frontend validation
        if (!validateForm()) return;

        setIsSubmitting(true);
        setErrors({}); // reset any leftover errors

        try {
            // 2️⃣ Save to Firestore
            await addDoc(collection(db, "contactMessages"), {
                name: form.name.trim(),
                email: form.email.trim(),
                message: form.message.trim(),
                createdAt: Timestamp.now(),
            });

            // 3️⃣ Show success UI
            setIsSubmitted(true);
            setForm({ name: "", email: "", message: "" });

            setTimeout(() => setIsSubmitted(false), 3000);
        }

        catch (error) {
            console.error("🔥 Firestore Error:", error);

            // Handle common Firestore errors
            let message = "Something went wrong. Please try again.";

            if (error.code === "unavailable") {
                message = "Network issue — please check your internet connection.";
            }

            if (error.code === "permission-denied") {
                message = "Your request was blocked. Firestore rules may be incorrect.";
            }

            if (error.code === "invalid-argument") {
                message = "Invalid data was sent. Please check your inputs and try again.";
            }

            alert(message);
        }

        finally {
            setIsSubmitting(false); // 4️⃣ Always runs
        }
    };



    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
            handleSubmit();
        }
    };

    const socialLinks = [
        {
            Icon: Instagram,
            label: "Instagram",
            color: "hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500",
            Href: "https://www.instagram.com/_bhavya.19?igsh=YnZ0MHdhM3ludW9r"
        },
        {
            Icon: Linkedin,
            label: "LinkedIn",
            color: "hover:bg-blue-700",
            Href: "https://www.linkedin.com/in/bhavya-kansara-8a332b373?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app"
        },
        {
            Icon: MessageCircle,
            label: "WhatsApp",
            color: "hover:bg-green-600",
            Href: "https://wa.me/917573071878?text=Hello!%20I'm%20Bhavya%20Kansara%20—%20Web%20Developer%20%26%20Graphic%20Designer.%20How%20can%20I%20assist%20you%20today%3F"
        }
    ];


    return (
        <section
            id="contact"
            className="w-full min-h-screen py-10 sm:pt-12 md:py-14 px-4 sm:px-6 md:px-12 lg:px-20 xl:px-32 bg-background text-text-light"
        >
            <SectionHeading SectionNumber="04" SectionTitle="Get in Touch" />

            <p className="sr-only">
                Contact Bhavya Kansara, professional web developer and graphic designer,
                for website development, UI design, branding, or freelance project inquiries.
            </p>


            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 mx-auto">

                {/* LEFT — FORM CARD */}
                <div
                    ref={leftCardRef}
                    className="card-anim bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl hover:shadow-primary/10 transition-all duration-500 hover:border-primary/30"
                >

                    {isSubmitted && (
                        <div className="mb-6 p-4 bg-primary/20 border border-primary/40 rounded-xl flex items-center gap-3 ">
                            <CheckCircle className="w-5 h-5 text-primary " />
                            <span className="text-text-light">Message sent successfully!</span>
                        </div>
                    )}

                    <div className="space-y-5 sm:space-y-6" onKeyPress={handleKeyPress}>

                        {/* Name */}
                        <div className="group">
                            <label className="text-text-light/90 block mb-2 font-medium text-sm">
                                Full Name <span className="text-accent">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                onFocus={() => setFocusedField("name")}
                                onBlur={() => setFocusedField(null)}
                                placeholder="John Doe"
                                className={`
                                    w-full px-4 py-3 rounded-xl bg-background/80 border-2 transition-all duration-300
                                    ${errors.name ? "border-red-500 text-red-400" :
                                        focusedField === "name" ? "border-primary shadow-primary/20 shadow-lg" :
                                            "border-primary/20 hover:border-primary/40"
                                    }
                                    text-text-light placeholder:text-text-light/40 focus:outline-none
                                `}
                            />
                            {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                        </div>

                        {/* Email */}
                        <div className="group">
                            <label className="text-text-light/90 block mb-2 font-medium text-sm">
                                Email <span className="text-accent">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                onFocus={() => setFocusedField("email")}
                                onBlur={() => setFocusedField(null)}
                                placeholder="john@example.com"
                                className={`
                                    w-full px-4 py-3 rounded-xl bg-background/80 border-2 transition-all duration-300
                                    ${errors.email ? "border-red-500 text-red-400" :
                                        focusedField === "email" ? "border-primary shadow-primary/20 shadow-lg" :
                                            "border-primary/20 hover:border-primary/40"
                                    }
                                    text-text-light placeholder:text-text-light/40 focus:outline-none
                                `}
                            />
                            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                        </div>

                        {/* Message */}
                        <div className="group">
                            <label className="text-text-light/90 block mb-2 font-medium text-sm">
                                Message <span className="text-accent">*</span>
                            </label>
                            <textarea
                                name="message"
                                rows="5"
                                value={form.message}
                                onChange={handleChange}
                                onFocus={() => setFocusedField("message")}
                                onBlur={() => setFocusedField(null)}
                                placeholder="Tell me about your project..."
                                className={`
                                    w-full px-4 py-3 rounded-xl bg-background/80 border-2 transition-all duration-300 resize-none
                                    ${errors.message ? "border-red-500 text-red-400" :
                                        focusedField === "message" ? "border-primary shadow-primary/20 shadow-lg" :
                                            "border-primary/20 hover:border-primary/40"
                                    }
                                    text-text-light placeholder:text-text-light/40 focus:outline-none
                                `}
                            ></textarea>
                            {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
                        </div>

                        {/* Submit Button */}
                        <Button
                            type="primary"
                            text={isSubmitting ? "Sending..." : "Send Message"}
                            icon={!isSubmitting && <Send className="w-5 h-5" />}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            classes="w-full"
                        />
                    </div>
                </div>

                {/* RIGHT — CONTACT INFO CARD */}
                <div
                    ref={rightCardRef}
                    className="card-anim bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl hover:shadow-accent/10 transition-all duration-500 hover:border-accent/30 flex flex-col justify-between"
                >

                    <div className="space-y-6 sm:space-y-8">

                        {/* Call */}
                        <div className="group border-b border-white/20 pb-6 hover:border-primary/40 transition-colors duration-300">
                            <h3 className="text-lg sm:text-xl font-semibold flex gap-2 items-center mb-2 group-hover:text-primary transition-colors">
                                <div className="p-2 rounded-lg bg-primary/10 border border-primary/20 group-hover:bg-primary/20 group-hover:scale-110 transition-all">
                                    <Phone className="w-5 h-5 text-accent" />
                                </div>
                                Call
                            </h3>
                            <a href="tel:+917573071878" className="text-text-light/80 hover:text-accent transition-colors text-sm sm:text-base">
                                +91 7573071878
                            </a>
                        </div>

                        {/* Email */}
                        <div className="group border-b border-white/20 pb-6 hover:border-primary/40 transition-colors duration-300">
                            <h3 className="text-lg sm:text-xl font-semibold flex gap-2 items-center mb-2 group-hover:text-primary transition-colors">
                                <div className="p-2 rounded-lg bg-primary/10 border border-primary/20 group-hover:bg-primary/20 group-hover:scale-110 transition-all">
                                    <Mail className="w-5 h-5 text-accent" />
                                </div>
                                Email
                            </h3>
                            <a href="mailto:contact@bhavyakansara.com" className="text-text-light/80 hover:text-accent break-all text-sm sm:text-base">
                                contact@bhavyakansara.com
                            </a>
                        </div>

                        {/* Social Links */}
                        <div className="pt-2">
                            <h3 className="text-lg sm:text-xl font-semibold mb-4">Connect With Me</h3>

                            <div className="flex flex-wrap gap-3 sm:gap-4">
                                {socialLinks.map(({ Icon, label, color, Href }) => (
                                    <a
                                        key={label}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={Href}
                                        aria-label={label}
                                        className={`
                                            group/social relative cursor-pointer w-12 h-12 p-3 
                                            rounded-xl bg-primary/10 border-2 border-primary/30
                                            hover:border-primary hover:scale-110 hover:rotate-6
                                            transition-all duration-300 active:scale-95
                                            ${color}
                                        `}
                                    >
                                        <Icon className="w-full h-full text-text-light group-hover/social:text-white" />
                                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-primary px-3 py-1.5 rounded-lg text-xs text-white opacity-0 group-hover/social:opacity-100 transition-opacity">
                                            {label}
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-white/10">
                        <p className="text-text-light/60 text-sm">
                            I'm always interested in hearing about new projects and opportunities.
                            Feel free to reach out!
                        </p>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default ContactSection;
